// components/AllRegistersManagerModal.js
import { Modal } from './modal.ts';
import { 
  fetchRegistersAdvanced, 
  deleteRegister, 
  fetchRegisters, fetchRegisterById, updateRegister
} from '../services/api.ts';
import { globalState } from "../framework/globalState.ts";
import { RegisterAssignmentModal } from "./RegisterAssignmentModal.ts";

const PAGE_SIZE = 50;

export class AllRegistersManagerModal {
  constructor() {
    this.siteId = globalState.currentSite?._id;
    this.modal = new Modal();
    this.currentPage = 1;
    this.total = 0;
    this.filters = {
      search: '',
      deviceId: '',
      dictionaryKey: '',
      address: ''
    };
    this.lastLoadedDevices = [];
  }

  async open() {
    if (!this.siteId) return alert('No site selected');
    this.currentPage = 1;
    const html = this.renderSkeleton();
    this.modal.open(html);
    await this.loadData();
    this.bindEvents();
  }

  renderSkeleton() {
    return `
      <div class="all-registers-manager">
        <div class="modal-header">
          <h3>All Registers Manager</h3>
          <small>Site: ${globalState.currentSite.name}</small>
        </div>
        <div class="modal-body">
          <div class="filters-bar">
            <input type="text" placeholder="Search name, key, address..." class="search-input" id="filter-search">
            <select id="filter-device"><option value="">All Devices</option></select>
            <select id="filter-dict"><option value="">All Presets</option></select>
            <input type="number" placeholder="Address" id="filter-address" style="width:100px">
            <button id="btn-refresh" class="btn-secondary">Refresh</button>
          </div>

          <div class="table-responsive">
            <table class="data-table">
              <thead>
                <tr>
                  <th><input type="checkbox" id="select-all"></th>
                  <th>Device</th>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Address</th>
                  <th>Type</th>
                  <th>Unit</th>
                  <th>Last Value</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody id="registers-tbody"></tbody>
            </table>
          </div>

          <div class="pagination">
            <button id="prev-page" disabled>Previous</button>
            <span id="page-info"></span>
            <button id="next-page">Next</button>
          </div>

          <div class="form-actions">
            <button id="btn-bulk-delete" class="btn-danger" disabled>Bulk Delete</button>
            <button id="btn-bulk-assign" class="btn-primary" disabled>
              <span class="iconify" data-icon="mdi:plus-circle-multiple"></span>
              Assign Selected to Device
            </button>
          </div>
          <div id="assign-device-picker" class="overlay" style="display:none">
            <div class="picker-card">
              <h4>Assign Registers to Device</h4>
              <p>Select target device:</p>
              <select id="target-device-select"></select>
              <div class="mt-3">
                <button id="confirm-assign" class="btn-primary">Assign</button>
                <button id="cancel-assign" class="btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async loadData() {
    const modalEl = this.modal.contentEl;
    const tbody = modalEl.querySelector('#registers-tbody');
    const pageInfo = modalEl.querySelector('#page-info');

    try {
      const params = {
        page: this.currentPage,
        limit: PAGE_SIZE
      };
      if (this.filters.search?.trim()) params.search = this.filters.search.trim();
      if (this.filters.deviceId) params.deviceId = this.filters.deviceId;
      if (this.filters.dictionaryKey) params.dictionaryKey = this.filters.dictionaryKey;
      if (this.filters.address) params.address = this.filters.address;

      const response = await fetchRegistersAdvanced(this.siteId, params);
      const { registers, total, devices = [], dicts = [] } = response; // ← response is already JSON

      this.total = total || 0;
      this.lastLoadedDevices = devices;

      // Populate filters
      this.populateDeviceFilter(devices);
      this.populateDictFilter(dicts);

      tbody.innerHTML = registers.map(r => `
        <tr data-id="${r._id}">
          <td><input type="checkbox" class="row-select"></td>
          <td><strong>${r.device?.name || '—'}</strong></td>
          <td>${r.name || '—'}</td>
          <td><code>${r.dictionaryKey || '—'}</code></td>
          <td>${r.address || '—'}</td>
          <td>${r.type || '—'}</td>
          <td>${r.unit || '—'}</td>
          <td>${r.value ?? '—'}</td>
          <td>
            <button class="btn-edit btn-sm">Edit</button>
            <button class="btn-delete btn-sm btn-danger">Delete</button>
          </td>
        </tr>
      `).join('');

      const totalPages = Math.ceil(this.total / PAGE_SIZE) || 1;
      pageInfo.textContent = `Page ${this.currentPage} of ${totalPages} (${this.total} total)`;
      modalEl.querySelector('#prev-page').disabled = this.currentPage === 1;
      modalEl.querySelector('#next-page').disabled = this.currentPage >= totalPages;

      this.updateActionButtons();
    } catch (err) {
      console.error("Load registers failed:", err);
      tbody.innerHTML = `<tr><td colspan="9">Error: ${err.message || 'Failed to load'}</td></tr>`;
    }
  }

  populateDeviceFilter(devices) {
    const select = this.modal.contentEl.querySelector('#filter-device');
    select.innerHTML = `<option value="">All Devices</option>` + 
      devices.map(d => `<option value="${d._id}">${d.name} (${d.role})</option>`).join('');
    select.value = this.filters.deviceId;
  }

  populateDictFilter(dicts) {
    const select = this.modal.contentEl.querySelector('#filter-dict');
    select.innerHTML = `<option value="">All Presets</option>` + 
      dicts.map(d => `<option value="${d.key}">[${d.group || '—'}] ${d.displayName}</option>`).join('');
    select.value = this.filters.dictionaryKey;
  }

  bindEvents() {
    const modalEl = this.modal.contentEl;

    modalEl.querySelector('#filter-search').oninput = (e) => {
      this.filters.search = e.target.value;
      this.currentPage = 1;
      this.loadData();
    };
    modalEl.querySelector('#filter-device').onchange = (e) => {
      this.filters.deviceId = e.target.value;
      this.currentPage = 1;
      this.loadData();
    };
    modalEl.querySelector('#filter-dict').onchange = (e) => {
      this.filters.dictionaryKey = e.target.value;
      this.currentPage = 1;
      this.loadData();
    };
    modalEl.querySelector('#filter-address').oninput = (e) => {
      this.filters.address = e.target.value;
      this.currentPage = 1;
      this.loadData();
    };

    modalEl.querySelector('#prev-page').onclick = () => {
      if (this.currentPage > 1) { this.currentPage--; this.loadData(); }
    };
    modalEl.querySelector('#next-page').onclick = () => {
      this.currentPage++;
      this.loadData();
    };
    modalEl.querySelector('#btn-refresh').onclick = () => this.loadData();

    // Edit button → fetch single register
    modalEl.querySelector('#registers-tbody').addEventListener('click', async (e) => {
      const row = e.target.closest('tr');
      if (!row) return;

      if (e.target.classList.contains('btn-edit')) {
        const regId = row.dataset.id;
        try {
          const reg = await fetchRegisterById(regId);
          new RegisterAssignmentModal(reg).open();
        } catch (err) {
            console.error("Failed to load register:", err);
            alert("Could not load register. It might be a legacy register without device.");
        }
      }

      if (e.target.classList.contains('btn-delete')) {
        if (!confirm('Delete this register permanently?')) return;
        try {
          await deleteRegister(row.dataset.id);
          this.loadData();
        } catch (err) {
          alert('Delete failed: ' + err.message);
        }
      }
    });

    modalEl.querySelector('#select-all').onchange = (e) => {
      modalEl.querySelectorAll('.row-select').forEach(cb => cb.checked = e.target.checked);
      this.updateActionButtons();
    };

    modalEl.addEventListener('change', (e) => {
      if (e.target.classList.contains('row-select')) this.updateActionButtons();
    });

    modalEl.querySelector('#btn-bulk-delete').onclick = async () => {
      const selected = Array.from(modalEl.querySelectorAll('.row-select:checked'))
        .map(cb => cb.closest('tr').dataset.id);
      if (!selected.length || !confirm(`Delete ${selected.length} registers permanently?`)) return;

      try {
        await Promise.all(selected.map(id => deleteRegister(id)));
        alert('Deleted successfully!');
        this.loadData();
      } catch (err) {
        alert('Bulk delete failed');
      }
    };

    // === BULK ASSIGN FEATURE ===
    modalEl.querySelector('#btn-bulk-assign').onclick = () => {
      const selectedIds = Array.from(modalEl.querySelectorAll('.row-select:checked'))
        .map(cb => cb.closest('tr').dataset.id);

      if (!selectedIds.length) return;

      // Show picker
      const picker = modalEl.querySelector('#assign-device-picker');
      const select = modalEl.querySelector('#target-device-select');
      select.innerHTML = '<option value="">Choose device...</option>' +
        this.lastLoadedDevices.map(d => 
          `<option value="${d._id}">${d.name} (${d.role})</option>`
        ).join('');

      picker.style.display = 'flex';
    };

    // Cancel assign
    modalEl.querySelector('#cancel-assign').onclick = () => {
      modalEl.querySelector('#assign-device-picker').style.display = 'none';
    };

    // Confirm assign
    modalEl.querySelector('#confirm-assign').onclick = async () => {
      const targetDeviceId = modalEl.querySelector('#target-device-select').value;
      if (!targetDeviceId) return alert('Please select a device');

      const selectedIds = Array.from(modalEl.querySelectorAll('.row-select:checked'))
        .map(cb => cb.closest('tr').dataset.id);

      if (!confirm(`Assign ${selectedIds.length} register(s) to this device?`)) return;

      try {
        // We'll use your existing API — assuming you have bulk update or loop is fine
        await Promise.all(selectedIds.map(regId => 
          updateRegister(regId, { deviceId: targetDeviceId })
        ));

        alert(`Successfully assigned ${selectedIds.length} registers!`);
        modalEl.querySelector('#assign-device-picker').style.display = 'none';
        this.loadData(); // refresh table
      } catch (err) {
        console.error(err);
        alert('Assignment failed: ' + err.message);
      }
    };
  }

  updateActionButtons() {
    const modalEl = this.modal.contentEl;
    const checked = modalEl.querySelectorAll('.row-select:checked').length;
    modalEl.querySelector('#btn-bulk-delete').disabled = checked === 0;
    modalEl.querySelector('#btn-bulk-assign').disabled = checked === 0;
  }
}